<template>
    <div class="billing-preview">
        <h2>Rendu de la facture</h2>
        <div class="bill">
            <div class="bill-header">
                <img class="edf-logo" alt="logo edf" src="static/img/big_logo.png"></img>
                <div class="bill-type">FACTURE</div>
            </div>
            <div class="client-info" v-if="client.name && client.billing_address">
                <div class="name" v-if="client.name">{{ client.name }}</div>
                <div class="address" v-if="client.billing_address">{{ client.billing_address }}</div>
            </div>
            <div class="period" v-if="startDateIsValid && endDateIsValid">Sur la période du <span class="bold">{{ billData.start_date }} au {{ billData.end_date }}</span></div>
            <div class="contact-edf" v-if="client.contact_edf">Contact EDF: <span class="bold">{{client.contact_edf}}</span></div>
            <div class="contact-client" v-if="client.contact_client">Contact client: <span class="bold">{{client.contact_client}}</span></div>
            <div class="siret" v-if="client.siret">Siret: <span class="bold">{{client.siret}}</span></div>
            <div class="vat-number" v-if="client.tva_number">Numéro de TVA: <span class="bold">{{client.tva_number}}</span></div>
            <el-row :gutter="0" class="data-table-labels">
                <el-col class="label-name" :span="6">Désignation</el-col>
                <el-col class="label-qty" :span="4">Quantité</el-col>
                <el-col class="label-vat" :span="8">TVA applicable (%)</el-col>
                <el-col class="label-pu" :span="3">PU HT</el-col>
                <el-col class="label-pt" :span="3">PT HT</el-col>
            </el-row>
            <div class="table-entry-container">
                <el-row
                    v-for="entry in dataForListTable"
                    :gutter="0"
                    :key="entry.name"
                    class="table-entry"
                >
                    <el-col class="name" :span="6">{{ entry.name }}</el-col>
                    <el-col class="qty" :span="4">{{ entry.quantity }}</el-col>
                    <el-col class="vat" :span="8">{{ entry.vat }}</el-col>
                    <el-col class="pu" :span="3">{{ entry.pu_ht }}</el-col>
                    <el-col class="pt" :span="3">{{ entry.pt_ht }}</el-col>
                </el-row>
            </div>
            <el-row
                v-for="entry in dataForSummary"
                :gutter="0"
                :key="entry.name"
                :class="{'darker': entry.last}"
                class="summary-table"
            >
                <el-col class="summary-label" :span="16">{{ entry.name }}</el-col>
                <el-col class="value" :span="8">{{ entry.value }} €</el-col>
            </el-row>
            <div class="bill-comment">{{ billData.comment }}</div>
        </div>
    </div>
</template>

<script>

export default {
    props: {
        billData: {
            type: Object,
            required: true
        },
        printable: {
            type: Boolean,
            default: false
        },
        clientBill: {
            type: Object,
            default: () => null
        },
        client: {
            type: Object,
            required: true
        }
    },
    computed: {
        startDateIsValid() {
            if (this.billData.start_date === null || this.billData.start_date === 'Invalid date' || this.billData.start_date === '') {
                return false;
            }
            return true;
        },
        endDateIsValid() {
            if (this.billData.end_date === null || this.billData.end_date === 'Invalid date' || this.billData.end_date === '') {
                return false;
            }
            return true;
        },
        summaryHT() {
            let total = 0;
            if (this.printable) {
                total += this.billData.saved_data.unit_price * this.billData.saved_data.simulation_number;
            } else {
                total += this.billData.amount || 0;
            }
            this.billData.extra_performance.forEach(extra => {
                total += (extra.unit_price_ht || 0) * (extra.quantity || 0);
            });
            return total;
        },
        summaryTTC() {
            let total = 0;
            if (this.printable) {
                total += this.billData.saved_data.unit_price * this.billData.saved_data.simulation_number * 1.20;
            } else {
                total += (this.billData.amount || 0) * 1.20;
            }
            this.billData.extra_performance.forEach(extra => {
                total += (extra.unit_price_ht || 0) * (extra.quantity || 0) + Math.round(
                    (extra.unit_price_ht || 0) * (extra.quantity || 0) * (extra.vat / 100) * 100) / 100;
            });
            return total;            
        },
        retrieveVATs() {
            let VATs = {
                20: {
                    name: 'TVA à 20%',
                    value: (this.billData.amount || 0) * 0.20
                }
            };
            this.billData.extra_performance.forEach(extra => {
                if (extra.vat && VATs[extra.vat]) {
                    VATs[extra.vat].value += Math.round((extra.unit_price_ht || 0) * (extra.quantity || 0) * (extra.vat / 100) * 100) / 100 ;
                } else {
                    VATs[extra.vat] = {
                        name: `TVA à ${extra.vat}%`,
                        value: Math.round((extra.unit_price_ht || 0) * (extra.quantity || 0) * (extra.vat / 100) * 100) / 100
                    };
                }
            });
            return Object.values(VATs);
        },
        dataForListTable() {
            let pu = `0 €`;
            let pt = `0 €`;
            let quantity = 0;
            let data = [];
            if (this.printable) {
                data.push(
                    {
                        name: 'Simulation',
                        quantity: this.billData.saved_data.simulation_number,
                        vat: 20,
                        pu_ht: this.billData.saved_data.unit_price,
                        pt_ht: this.billData.saved_data.simulation_number * this.billData.saved_data.unit_price
                    }
                );
            } else {
                if (this.billData.simulation_number && this.billData.simulation_number > 0) {
                    quantity = this.billData.simulation_number;
                    if (this.billData.amount) {
                        pu = `${this.billData.amount / this.billData.simulation_number} €`;
                        pt = `${this.billData.amount} €`;
                    }
                }
                data.push(
                    {
                        name: 'Simulation',
                        quantity: quantity,
                        vat: 20,
                        pu_ht: pu,
                        pt_ht: pt
                    }
                );
            }
            if (parseInt(this.billData.free_simulation_number) > 0) {
                data.push({
                    name: 'Simulation offerte',
                    quantity: this.billData.free_simulation_number,
                    vat: 20,
                    pu_ht: '0 €',
                    pt_ht: '0 €',
                });
            }
            if (this.billData.extra_performance.length) {
                this.billData.extra_performance.forEach((extra, index) => {
                    let priceHT = '0 €';
                    let totalHT = '0 €';
                    let extraName = `Prestation ${index + 1}`;
                    if (extra.unit_price_ht) {
                        priceHT = `${extra.unit_price_ht} €`;
                        if (extra.quantity) {
                            totalHT = `${extra.unit_price_ht * extra.quantity} €`;
                        }
                    }
                    if (extra.label) {
                        extraName = extra.label;
                    }
                    data.push({
                        name: extraName,
                        quantity: extra.quantity,
                        vat: extra.vat,
                        pu_ht: priceHT,
                        pt_ht: totalHT,
                    });
                });
            }
            return data;
        },
        dataForSummary() {
            return [
                {
                    name: 'Reste à payer HT',
                    value: this.summaryHT
                },
                ...this.retrieveVATs,
                {
                    last: true,
                    name: 'Total TTC',
                    value: this.summaryTTC
                },
            ];
        }
    },
};
</script>

<style lang="scss">
.billing-preview {
    width: 100%;
    h2 {
        font-size: 14px;
    }
    .bill {
        width: 100%;
        padding: 20px 30px;
        border-radius: 4px;
        border: 1px #CCC solid;
        .bill-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            .edf-logo {
                width: 60px;
            }
            .bill-type {
                background-color: #ececec;
                padding: 10px;
                text-align: center;
                width: 50%;
                font-weight: bold;
            }
        }
        .client-info {
            margin-top: 50px;
            margin-left: 50%;
            .name, .address, .zip-code {
                font-size: 12px;
                font-weight: bold;
            }
        }
        .period {
            margin-top: 20px;
        }
        .period, .contact-edf, .contact-client, .siret, .vat-number {
            font-size: 12px;
            .bold {
                font-weight: bold;
            }
        }
        .data-table-labels {
            margin-top: 20px;
            padding: 0 10px;
        }
        .table-entry-container {
            margin-bottom: 20px;
            .table-entry {
                padding: 5px 10px;
                background-color: #ececec;
                display: flex;
                align-items: center;
                .qty, .pu, .pt, .vat {
                    font-weight: bold;
                }
                .name {
                    word-break: break-word;
                }
            }
        }
        .data-table-labels, .table-entry {
            font-size: 12px;
            margin-bottom: 1px;
            .label-name, .name {
                text-align: left;
            }
            .label-qty, .label-pu, .label-pt, .qty, .pu, .pt {
                text-align: right;
            }
            .label-vat, .vat {
                text-align: center;
            }
        }
        .summary-table {
            margin-bottom: 1px;
            margin-left: 50%;
            font-size: 12px;
            background-color: #ececec;
            padding: 5px 10px;
            display: flex;
            align-items: center;
            .summary-label {
                word-break: break-word;
            }
            &.darker {
                background-color: #e1dfe0;
            }
            .value {
                text-align: right;
                font-weight: bold;
            }
        }
        .bill-comment {
            font-size: 12px;
            margin-top: 20px;
            margin-bottom: 30px;
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/components/page/Facturation/preview.vue